import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useSearchParams, type Location } from 'react-router-dom'
import { Check, Clock, Lock, ShieldCheck, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { useAuth } from '@/hooks/use-auth'
import { callableErrorMessage } from '@/lib/callable-errors'
import { createSumsubLink } from '@/lib/callables'
import { openSignIn } from '@/lib/signin-navigation'
import type { VerifyIdentityIntent } from '@/lib/verify-identity-navigation'
import { cn } from '@/lib/utils'

/**
 * Identity verification overlay — Sumsub document check only (no ConnectID).
 * Opened via `/verify-identity` while the previous screen stays underneath.
 * Mobile: bottom sheet (flush), matching Settings / property profile chrome.
 */
export function VerifyIdentityModal() {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const next = useMemo(() => searchParams.get('next') || '/search', [searchParams])
  const background = (location.state as { background?: Location } | null)?.background
  const intent =
    (location.state as { verifyIntent?: VerifyIdentityIntent } | null)?.verifyIntent ?? 'default'
  const fromCreateBiddle = intent === 'create-biddle' || next.includes('create=1')
  const fromLetterClaim =
    intent === 'letter-claim' || next.startsWith('/o/') || next === '/o'

  const { user, profile, isGuest, isLoading } = useAuth()

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [permalink, setPermalink] = useState<string | null>(null)
  const [awaiting, setAwaiting] = useState(false)
  const [popupBlocked, setPopupBlocked] = useState(false)
  const [entered, setEntered] = useState(false)

  const verified = Boolean(profile?.isIdentityVerified)
  const signedIn = Boolean(user) && !isGuest

  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dismiss()
    }
    window.addEventListener('keydown', onKey)
    const id = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => setEntered(true))
    })
    return () => {
      document.body.style.overflow = prev
      window.removeEventListener('keydown', onKey)
      window.cancelAnimationFrame(id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (verified) {
      setAwaiting(false)
      setBusy(false)
    }
  }, [verified])

  useEffect(() => {
    if (isLoading || signedIn) return
    openSignIn(navigate, location, `${location.pathname}${location.search}`)
  }, [isLoading, signedIn, navigate, location])

  function dismiss() {
    setEntered(false)
    window.setTimeout(() => {
      if (background) {
        navigate(background, { replace: true })
        return
      }
      navigate(next.startsWith('/o') ? next : next || '/search', { replace: true })
    }, 200)
  }

  async function startVerification() {
    setBusy(true)
    setError(null)
    setPopupBlocked(false)
    try {
      const { permalink: url } = await createSumsubLink()
      setPermalink(url)
      setAwaiting(true)
      const opened = window.open(url, '_blank', 'noopener,noreferrer')
      if (!opened) {
        setPopupBlocked(true)
      }
    } catch (e) {
      setError(callableErrorMessage(e))
      setAwaiting(false)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      className={cn(
        'fixed inset-0 z-[90] flex items-end justify-center bg-navy/70 p-0 backdrop-blur-sm transition-opacity duration-200 ease-out sm:items-center sm:p-4',
        entered ? 'opacity-100' : 'opacity-0',
      )}
      role="dialog"
      aria-modal="true"
      aria-labelledby="verify-identity-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) dismiss()
      }}
    >
      <div
        className={cn(
          'relative flex max-h-[min(92dvh,800px)] w-full max-w-lg flex-col overflow-hidden bg-white shadow-2xl transition-transform duration-200 ease-out',
          // Mobile bottom sheet: square bottom edge so it docks flush (not a floating card).
          'rounded-t-3xl rounded-b-none sm:rounded-3xl',
          entered ? 'translate-y-0' : 'translate-y-full sm:translate-y-2 sm:scale-[0.98]',
          entered ? 'opacity-100' : 'opacity-0 sm:opacity-0',
        )}
      >
        <button
          type="button"
          onClick={dismiss}
          className="absolute right-3 top-3 z-10 rounded-lg p-2 text-muted transition hover:bg-surface hover:text-charcoal"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>

        {isLoading || !signedIn ? (
          <div className="flex min-h-[16rem] items-center justify-center py-16">
            <Spinner />
          </div>
        ) : verified ? (
          <>
            <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-4 pt-7 pr-12">
              <h2
                id="verify-identity-title"
                className="flex items-center gap-2 text-lg font-semibold text-navy"
              >
                <ShieldCheck className="h-5 w-5 text-success" />
                You’re verified
              </h2>
              <p className="mt-2 text-sm text-muted">
                Your identity is verified. You can create Biddles, buy letter credits, and claim
                properties.
              </p>
            </div>
            <div className="shrink-0 border-t border-border px-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-4">
              <Button type="button" variant="primary" className="w-full" onClick={dismiss}>
                Continue
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-4 pt-7 pr-12">
              <h2
                id="verify-identity-title"
                className="flex items-center gap-2 text-lg font-semibold text-navy"
              >
                <ShieldCheck className="h-5 w-5 text-biddle-blue" />
                {fromCreateBiddle ? 'You’re so close!' : 'Verify your identity'}
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-muted">
                {fromCreateBiddle
                  ? 'One quick identity verification and you can send this Biddle — then submit as many as you like.'
                  : fromLetterClaim
                    ? 'To keep Biddle safe for everyone, we need to verify your identity before you can add your property. Buyers must verify their identity before they can create Biddles.'
                    : 'To protect buyers and property owners, we need to verify your identity before you can make Biddles, buy letter credits, or add your property.'}
              </p>

              <ul className="mt-5 space-y-3 text-sm text-charcoal">
                <Benefit
                  icon={Lock}
                  title="Secure & private"
                  description="Your data is encrypted and protected"
                />
                <Benefit
                  icon={Clock}
                  title="Quick process"
                  description="Typically takes less than 2 minutes"
                />
                <Benefit
                  icon={Check}
                  title="One-time verification"
                  description={
                    fromCreateBiddle
                      ? 'Verify once, then submit unlimited Biddles'
                      : fromLetterClaim
                        ? 'Verify once — then claim this property and manage any future interest'
                        : 'Verify once, use across all properties'
                  }
                />
              </ul>

              {awaiting ? (
                <div className="mt-5 rounded-xl border border-border bg-surface px-4 py-3 text-sm text-muted">
                  <p className="font-medium text-navy">Waiting for verification…</p>
                  <p className="mt-1">
                    Finish the document check in the other tab. This window updates automatically
                    when you’re done.
                  </p>
                  {popupBlocked && permalink ? (
                    <p className="mt-2">
                      Pop-up blocked.{' '}
                      <a
                        href={permalink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-semibold text-biddle-blue hover:underline"
                      >
                        Open verification
                      </a>
                    </p>
                  ) : null}
                </div>
              ) : null}

              {error ? <p className="mt-4 text-sm text-danger">{error}</p> : null}
            </div>

            <div className="shrink-0 space-y-3 border-t border-border px-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-4">
              <div className="flex items-center gap-3">
                <Button type="button" variant="ghost" className="shrink-0" onClick={dismiss}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  className="min-w-0 flex-1"
                  disabled={busy}
                  onClick={() => void startVerification()}
                >
                  {busy ? <Spinner className="border-white border-t-transparent" /> : null}
                  {awaiting ? 'Open verification again' : 'Continue'}
                </Button>
              </div>
              <p className="text-center text-xs leading-relaxed text-muted">
                You’ll complete verification with our identity partner (Sumsub) in a secure browser
                tab. You’ll need a photo ID and a short selfie check.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function Benefit({
  icon: Icon,
  title,
  description,
}: {
  icon: typeof Lock
  title: string
  description: string
}) {
  return (
    <li className="flex gap-3">
      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-biddle-blue/10 text-biddle-blue">
        <Icon className="h-4 w-4" aria-hidden />
      </span>
      <span>
        <span className="block font-semibold text-navy">{title}</span>
        <span className="text-muted">{description}</span>
      </span>
    </li>
  )
}
